// Authenticated avatar loading.
//
// Profile photos sit behind the same bearer token as everything else, so a
// plain <img src> can never load one: the browser would send no Authorization
// header. The bytes are fetched through the authenticated Axios instance and
// rendered from a short-lived object URL instead. No token is ever placed in a
// URL, and no cookie is introduced.
//
// Every fetch is remembered for the session, keyed by member and version, so a
// feed of twenty cards asks for each avatar once - including the answer "this
// member has no photo", which is exactly as worth remembering.
import { getAvatarBlob } from './api';

// The only shape of reference we will act on. A photoUrl is generated by our
// own backend; anything else - an absolute URL, another origin, a tracking
// pixel - is ignored rather than loaded.
const TRUSTED_PHOTO_URL = /^\/api\/users\/[0-9a-fA-F-]{36}\/photo(\?|$)/;

// Used when a surface knows the member id but carries no photoUrl (the
// connection list and the person picker keep a locked payload shape).
export const LIVE_VERSION = 'live';

const cache = new Map();     // `${userId}:${version}` -> object URL, or null
const inflight = new Map();

export const versionFromPhotoUrl = (photoUrl) => {
  if (typeof photoUrl !== 'string' || !TRUSTED_PHOTO_URL.test(photoUrl)) return null;
  const query = photoUrl.indexOf('?');
  if (query === -1) return LIVE_VERSION;
  const params = new URLSearchParams(photoUrl.slice(query + 1));
  return params.get('v') || LIVE_VERSION;
};

const releaseSuperseded = (userId, keep) => {
  const prefix = `${userId}:`;
  for (const [key, url] of cache) {
    if (key === keep || !key.startsWith(prefix)) continue;
    if (url) URL.revokeObjectURL(url);
    cache.delete(key);
  }
};

// Forget everything held for one member, revoking their object URLs, so the
// next render fetches the replacement rather than showing the old photo.
export const invalidateAvatar = (userId) => {
  if (!userId) return;
  releaseSuperseded(userId, null);
  for (const key of [...inflight.keys()]) {
    if (key.startsWith(`${userId}:`)) inflight.delete(key);
  }
};

export const loadAvatar = (userId, photoUrl) => {
  if (!userId) return Promise.resolve(null);

  // null means the server has told us there is no photo: nothing to ask for.
  if (photoUrl === null) return Promise.resolve(null);

  const version = photoUrl === undefined ? LIVE_VERSION : versionFromPhotoUrl(photoUrl);
  // An untrusted reference is not followed.
  if (version === null) return Promise.resolve(null);

  const key = `${userId}:${version}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);

  const pending = getAvatarBlob(userId, version === LIVE_VERSION ? null : version)
    .then((blob) => {
      const url = blob && blob.size ? URL.createObjectURL(blob) : null;
      releaseSuperseded(userId, key);
      cache.set(key, url);
      return url;
    })
    .catch(() => {
      // A missing or refused avatar is not an error the member should see.
      cache.set(key, null);
      return null;
    })
    .finally(() => inflight.delete(key));

  inflight.set(key, pending);
  return pending;
};

// Test seam only.
export const __resetAvatarCache = () => {
  for (const url of cache.values()) if (url) URL.revokeObjectURL(url);
  cache.clear();
  inflight.clear();
};
